import React, { useState, useEffect, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import Grid2 from "@mui/material/Grid2";

import GeneralLayout from "components/PageLayouts/GeneralLayout";
import GenericRecordSummary from "components/Records/RecordDisplay/GenericRecordSummary";
import EditableFieldsTable from "components/Records/RecordDisplay/EditableFieldsTable";

import { getProfileDisplay, profileAPI, updateUserField } from "api/Accounts/AccountsAPI";
import { ProfileDisplay, Profile as ProfileType, User } from "api/Accounts/AccountsInterfaces";
import BasePaper from "components/Base/BasePaper";

const Profile: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileDisplay | null>(null);
  const [profileID, setProfileID] = useState<number | null>(null);

  const fetchProfile = useCallback(async () => {
    try {
      const profileData = await getProfileDisplay();
      setProfile(profileData);
      setProfileID(Number(profileData.id));
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const userFields: (keyof User)[] = [
    "username",
    "email",
    "first_name",
    "last_name",
  ];

  const profileFields: (keyof ProfileType)[] = [
    "phone_number",
    "organisation",
    "department",
    "position_title",
    "role",
    "permissions",
  ];

  const onUpdate = async (
    id: number,
    field: keyof ProfileDisplay,
    value: any
  ): Promise<void> => {
    if (!profile) return;

    const previousProfile = profile;

    // optimistic update
    setProfile((prev) =>
      prev ? { ...prev, [field]: value } : prev
    );

    try {
      if (userFields.includes(field as keyof User)) {
        await updateUserField(field, value);
        await fetchProfile();
        return;
      }

      if (profileFields.includes(field as keyof ProfileType)) {
        await profileAPI.updateField(id, field, value);
        await fetchProfile();
        return;
      }

      console.warn(`Unknown field passed to onUpdate: ${String(field)}`);
      await fetchProfile();
    } catch (error) {
      console.error(`Failed to update ${String(field)}:`, error);

      // rollback on failure
      setProfile(previousProfile);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return <div>Profile not found</div>;
  }

  return (
    <GeneralLayout>
      <GenericRecordSummary
        record={profile}
        titleField={["username"]}
        onUpdate={onUpdate}
        loading={loading}
      />

      <Grid2 container>
        <Grid2 size={6}>
          <BasePaper>
            <EditableFieldsTable
              title={"Personal Details"}
              record={profile}
              fields={[
                { field: "first_name", label: "First Name" },
                { field: "last_name", label: "Last Name" },
                { field: "email", label: "Email" },
                { field: "phone_number", label: "Phone Number" },
              ]}
              onSave={(field, value) => onUpdate(Number(profileID), field, value)}
            />
          </BasePaper>
        </Grid2>

        <Grid2 size={6}>
          <BasePaper>
            <EditableFieldsTable
              title={"Role Details"}
              record={profile}
              fields={[
                { field: "organisation", label: "Organisation", editable: false },
                { field: "department", label: "Department" },
                { field: "position_title", label: "Position Title" },
                { field: "role", label: "Role", editable: false},
              ]}
              onSave={(field, value) => onUpdate(Number(profileID), field, value)}
            />
          </BasePaper>
        </Grid2>
      </Grid2>
    </GeneralLayout>
  );
};

export default Profile;